import React from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";
import "./comparisonRadarChart.css";

interface RadarDataPoint {
  subject: string;
  min?: number;
  max?: number;
  [key: string]: string | number | undefined;
}

interface RadarDataSet {
  dataKey: string;
  fillColor: string;
  strokeColor: string;
  fillOpacity?: number;
  strokeWidth?: number;
}

interface TableRow {
  label: string;
  values: (number | string)[];
  unit?: string;
}

interface ComparisonRadarChartProps {
  title: string;
  radarData: RadarDataPoint[];
  radarDatasets: RadarDataSet[];
  tableData: TableRow[];
  colors: string[];
  legendLabels: string[];
  className?: string;
}

let measureCanvas: HTMLCanvasElement | null = null;
let cachedTickFont: string | null = null;

const getTickFont = (): string => {
  if (!cachedTickFont) {
    let fontSize = 12;
    if (typeof window !== "undefined" && window.getComputedStyle) {
      const rootFontSize = parseFloat(
        window.getComputedStyle(document.documentElement).fontSize
      );
      fontSize = 0.75 * (rootFontSize || 16);
    }
    cachedTickFont = `500 ${fontSize}px Inter, sans-serif`;
  }
  return cachedTickFont;
};

const measureTextWidth = (text: string): number => {
  if (!text) return 0;
  if (typeof document === "undefined") return text.length * 7;
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const context = measureCanvas.getContext("2d");
  if (!context) return text.length * 7;
  context.font = getTickFont();
  return context.measureText(text).width;
};

const ComparisonRadarChart: React.FC<ComparisonRadarChartProps> = ({
  title,
  radarData,
  radarDatasets,
  tableData,
  colors,
  legendLabels,
  className = "",
}) => {
  const darkenColor = (color: string): string => {
    if (color.startsWith("var(")) {
      if (color.includes("--color-light-gray")) return "#d0cfcf";
      if (color.includes("--color-gray")) return "#b8b8b8";
      if (color.includes("--color-dark-blue")) return "#1e3c5a";
      if (color.includes("--color-blue")) return "#3d6b9c";
      if (color.includes("--color-light-blue")) return "#2a8fa6";
      if (color.includes("--color-teal")) return "#026d7a";
      if (color.includes("--color-green")) return "#1a8b60";
      if (color.includes("--color-orange")) return "#c5471a";
      if (color.includes("--color-yellow")) return "#b8750a";
      return "#999999";
    }
    return color;
  };

  const shapeColor = darkenColor("var(--color-light-gray)");

  const normalizeData = (rawData: RadarDataPoint[]) => {
    return rawData.map((item) => {
      const normalizedItem = { ...item };
      const min = item.min || 0;
      const max = item.max || 100;

      radarDatasets.forEach((dataset) => {
        const value = item[dataset.dataKey] as number;
        if (typeof value === "number") {
          let normalizedValue;

          if (min < 0 && max < 0) {
            normalizedValue = ((min - value) / (min - max)) * 100;
          } else {
            normalizedValue = ((value - min) / (max - min)) * 100;
          }

          normalizedItem[dataset.dataKey] = Math.max(
            0,
            Math.min(100, normalizedValue)
          );
          normalizedItem[`_raw_${dataset.dataKey}`] = value;
        }
      });

      return normalizedItem;
    });
  };

  const getValueColor = (value: number | string): string => {
    const numericValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.-]/g, ""))
        : value;

    if (numericValue > 0) {
      return "var(--color-green)";
    } else if (numericValue < 0) {
      return "var(--color-orange)";
    } else {
      return "var(--color-dark-blue)";
    }
  };

  const tickPointsRef = React.useRef<Record<number, { x: number; y: number }>>({});

  const chartAreaRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);
  const [, setFontsReady] = React.useState(false);

  React.useEffect(() => {
    const node = chartAreaRef.current;
    if (!node) return;
    const updateWidth = () => setChartWidth(node.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.ready) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (cancelled) return;
      cachedTickFont = null;
      setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const CustomTick = (props: any) => {
    const { payload, x, y, textAnchor, index } = props;
    const text = payload.value;

    if (
      typeof x === "number" &&
      typeof y === "number" &&
      typeof index === "number"
    ) {
      tickPointsRef.current[index] = { x, y };
    }

    const edgePadding = 4;
    let availableWidth = Number.POSITIVE_INFINITY;
    if (chartWidth > 0 && typeof x === "number") {
      if (textAnchor === "end") {
        availableWidth = x - edgePadding;
      } else if (textAnchor === "start") {
        availableWidth = chartWidth - x - edgePadding;
      } else {
        availableWidth =
          Math.min(x, chartWidth - x) * 2 - edgePadding * 2;
      }
    }

    let lines: string[];
    if (measureTextWidth(text) <= availableWidth) {
      lines = [text];
    } else {
      lines = [];
      let currentLine = "";
      String(text)
        .split(" ")
        .forEach((word: string) => {
          const testLine = currentLine + (currentLine ? " " : "") + word;
          if (measureTextWidth(testLine) <= availableWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        });
      if (currentLine) lines.push(currentLine);
    }

    return (
      <text
        x={x}
        y={y}
        fill="var(--color-dark-blue)"
        fontSize="0.75rem"
        fontWeight={500}
        textAnchor={textAnchor}
        dominantBaseline="middle"
      >
        {lines.length === 1 ? (
          text
        ) : (
          lines.map((line, lineIndex) => (
            <tspan
              key={lineIndex}
              x={x}
              dy={lineIndex === 0 ? -(lines.length - 1) * 6 : 12}
            >
              {line}
            </tspan>
          ))
        )}
      </text>
    );
  };

  const renderRadarLabel = (dataKey: string, color: string) => (props: any) => {
    const { x, y, payload, index } = props;
    const rawValue =
      payload?.[`_raw_${dataKey}`] ??
      normalizedData[index]?.[`_raw_${dataKey}`];
    if (typeof rawValue !== "number" || !isFinite(x) || !isFinite(y))
      return null;

    let lx = x;
    let ly = y;
    const tickPoints = Object.values(tickPointsRef.current);
    if (tickPoints.length > 0) {
      const cx = tickPoints.reduce((s, p) => s + p.x, 0) / tickPoints.length;
      const cy = tickPoints.reduce((s, p) => s + p.y, 0) / tickPoints.length;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) {
        const offset = 12;
        lx = x + (dx / dist) * offset;
        ly = y + (dy / dist) * offset;
      }
    }

    const formatted = Number.isInteger(rawValue)
      ? String(rawValue)
      : rawValue.toFixed(2);

    const unit =
      tableData.find((row) => row.label === payload?.subject)?.unit || "";

    return (
      <text
        x={lx}
        y={ly}
        fill={color}
        fontSize="0.7rem"
        fontWeight={600}
        textAnchor="middle"
        dominantBaseline="middle"
        stroke="var(--color-white)"
        strokeWidth="3"
        paintOrder="stroke"
      >
        {formatted}
        {unit}
      </text>
    );
  };

  const normalizedData = normalizeData(radarData);

  return (
    <div className={`comparisonRadarChart-container ${className}`}>
      <svg
        className="comparisonRadarChart-svg-top-left"
        width="160"
        height="40"
        viewBox="0 0 111 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M65.0082 15.7865C100.199 12.0577 108.741 3.83731 110.135 0.914062L0.00927734 0.914071L0.00927896 16.9908C8.37324 18.1601 30.8655 19.4042 65.0082 15.7865Z"
          fill={shapeColor}
        />
      </svg>

      <svg
        className="comparisonRadarChart-svg-bottom-right"
        width="280"
        height="70"
        viewBox="0 0 186 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_54620_1532)">
          <path
            d="M102.58 15.493C38.5252 15.493 7.8007 28.1851 0.445312 34.5312H185.801V0.685547C184.75 5.62137 166.635 15.493 102.58 15.493Z"
            fill={shapeColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_54620_1532">
            <path
              d="M0.445312 0.685547H185.801V24.5312C185.801 30.0541 181.324 34.5312 175.801 34.5312H0.445312V0.685547Z"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>

      {title && (
        <div className="comparisonRadarChart-header">
          <span className="comparisonRadarChart-subtitle">Comparison chart</span>
          <h3 className="comparisonRadarChart-title">{title.toUpperCase()}</h3>
        </div>
      )}
      
      <div className="comparisonRadarChart-content">
        <div className="comparisonRadarChart-left">
          <div className="comparisonRadarChart-table-container">
            <table className="comparisonRadarChart-table">
              <thead>
                <tr>
                  <th className="comparisonRadarChart-table-header-empty"></th>
                  {colors.map((color, index) => (
                    <th
                      key={index}
                      className="comparisonRadarChart-table-header"
                    >
                      <div
                        className="comparisonRadarChart-color-circle"
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="comparisonRadarChart-table-model-name">
                        {legendLabels[index]}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="comparisonRadarChart-table-label">
                      {row.label}
                    </td>
                    {row.values.map((value, valueIndex) => (
                      <td
                        key={valueIndex}
                        className="comparisonRadarChart-table-value"
                        style={{ color: getValueColor(value) }}
                      >
                        {value}
                        {row.unit || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="comparisonRadarChart-right">
          <div ref={chartAreaRef} style={{ width: "100%", height: "480px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                data={normalizedData}
                margin={{ top: 60, right: 70, bottom: 60, left: 70 }}
              >
                <PolarGrid
                  stroke="var(--color-white)"
                  strokeDasharray="5,5"
                  radialLines={false}
                  className="comparisonRadarChart-grid"
                />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={<CustomTick />}
                  className="comparisonRadarChart-angle-axis"
                />
                {radarDatasets.map((dataset, index) => (
                  <Radar
                    key={index}
                    dataKey={dataset.dataKey}
                    stroke={dataset.strokeColor}
                    fill={dataset.fillColor}
                    fillOpacity={dataset.fillOpacity || 0.3}
                    strokeWidth={dataset.strokeWidth || 2}
                    label={renderRadarLabel(
                      dataset.dataKey,
                      colors[index % colors.length]
                    )}
                    className="comparisonRadarChart-radar"
                  />
                ))}
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonRadarChart;
